<template>
  <full-screen :title="t('close-delivery.title')" @close="router.back()">
    <div class="max-w-[33rem] px-4 m-auto mt-4">
      <div v-if="hasEnabledServices">
        <delivery-selector
          :virtual-brand-deliveries="virtualBrandsClosingTimes"
          :current-time="currentTime"
          show-closed-until
          @close-delivery="(brandId, company) => setDeliveryToClose(brandId, company)"
          @open-delivery="(brandId, company) => openDelivery(brandId, company)"
          @close-brand="setBrandToClose"
          @open-brand="openBrand"
        />
        <div class="border-t border-n-700">
          <div class="flex flex-row items-center">
            <div class="flex-1">
              <l-button
                type="text"
                class="pl-0 !text-n-0 !hover:text-v-600"
                @click="() => (mainState == 1 ? setAllDeliveriesToClose() : openAllDeliveries())"
              >
                {{ mainState == 1 ? t('close-delivery.close-all') : t('close-delivery.open-all') }}
              </l-button>
            </div>
            <div class="flex-none">
              <l-switch v-model="mainState" :enable-on-partial-state="true" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="mt-12 mb-4 text-n-200 text-center">
        <l-icon name="bike" class="mx-auto w-10 h-10" />
        <span class="inline-block my-2 uppercase heading-3">
          {{ t('close-delivery.no-services-message') }}
        </span>
      </div>
    </div>
    <closing-time-modal
      v-if="closingBrandsSelected"
      :loading="isClosing"
      @close="closingBrandsSelected = false"
      @confirm="startClosing"
    />
  </full-screen>
</template>

<script setup lang="ts">
import { differenceInDays, formatDistance, parseISO } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LIcon, LSwitch, useDialog } from '@last/core-ui/paprika'

import FullScreen from '@/components/FullScreen.vue'
import ClosingTimeModal from '@/modules/delivery/ClosingTimeModal.vue'
import DeliverySelector from '@/modules/delivery/DeliverySelector.vue'
import { translateCompany } from '@/modules/delivery/product-name.utils'
import { useToast } from '@/plugins/toast/toast'
import { useDeliveryCompaniesStore } from '@/store/deliveryCompanies'
import type { ClosingTime } from '@/types/virtualBrands'
import { currentTime } from '@/utils/currentTime'

const router = useRouter()
const toast = useToast()
const { t } = useI18n()

const deliveryCompaniesStore = useDeliveryCompaniesStore()

const { virtualBrandsClosingTimes } = storeToRefs(deliveryCompaniesStore)
const { closeDeliveries, openDeliveries } = deliveryCompaniesStore

const selectedBrandAndCompanies = ref<Record<string, string[]>>({})
const closingBrandsSelected = ref(false)
const isClosing = ref(false)
const dialog = useDialog()

const totalEnabledServices = computed(() => {
  return Object.values(virtualBrandsClosingTimes.value)
    .map(vb => Object.keys(vb.externalDeliveryCompaniesClosingTimes).length)
    .reduce((total, count) => total + count, 0)
})

const hasEnabledServices = computed(() => {
  return totalEnabledServices.value > 0
})

type SwitchModelType = boolean | 0 | 1 | 2
const mainState = computed({
  get: (): SwitchModelType => {
    let generalState: SwitchModelType | undefined
    Object.values(virtualBrandsClosingTimes.value).forEach(brand => {
      const length = Object.values(brand.externalDeliveryCompaniesClosingTimes).length
      const times = Object.values(brand.externalDeliveryCompaniesClosingTimes).filter(time => {
        return time
      })

      const brandValue = times.length === 0 ? 1 : times.length === length ? 0 : 2
      if (typeof generalState === 'undefined') {
        generalState = brandValue
      } else if (generalState !== brandValue) {
        generalState = 2
      }
    })

    return generalState || 0
  },
  set: (val: boolean) => {
    if (val) {
      openAllDeliveries()
    } else {
      setAllDeliveriesToClose()
    }
  }
})

function setAllDeliveriesToClose() {
  virtualBrandsClosingTimes.value.forEach(brand => {
    selectedBrandAndCompanies.value[brand.id] = Object.keys(
      brand.externalDeliveryCompaniesClosingTimes
    )
  })
  closingBrandsSelected.value = true
}

function setBrandToClose(brandId: string) {
  selectedBrandAndCompanies.value[brandId] = Object.keys(
    virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)!
      .externalDeliveryCompaniesClosingTimes
  )
  closingBrandsSelected.value = true
}

async function openAllDeliveries(): Promise<void> {
  const deliveriesToOpen: Record<string, string[]> = {}
  Object.values(virtualBrandsClosingTimes.value).forEach(brand => {
    deliveriesToOpen[brand.id] = Object.keys(
      virtualBrandsClosingTimes.value.find(b => b.id === brand.id)!
        .externalDeliveryCompaniesClosingTimes
    )
  })

  const dialogInstance = dialog({
    icon: 'status',
    iconAnimation: 'animate-spin',
    title: t('close-delivery.opening'),
    noLabels: true,
    hiddenClose: true
  })
  try {
    const { failed } = await openDeliveries(deliveriesToOpen)
    showFailedNotifications(failed, 'open')

    if (failed.length === 0) {
      toast({
        type: 'success',
        title: t('close-delivery.all-deliveries-opened')
      })
    }
  } catch {
    showConnectionErrorToast()
  } finally {
    dialogInstance?.close()
  }
}

async function openBrand(brandId: string) {
  const deliveriesToOpen: Record<string, string[]> = {}
  deliveriesToOpen[brandId] = Object.keys(
    virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)!
      .externalDeliveryCompaniesClosingTimes
  )
  const dialogInstance = dialog({
    icon: 'status',
    iconAnimation: 'animate-spin',
    title: t('close-delivery.opening'),
    noLabels: true,
    hiddenClose: true
  })
  try {
    const { failed } = await openDeliveries(deliveriesToOpen)
    showFailedNotifications(failed, 'open')

    if (failed.length === 0) {
      const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
      const brandName = brand?.brandName ?? ''

      toast({
        type: 'success',
        title: t('close-delivery.brand-opened', { brandName })
      })
    }
  } catch {
    showConnectionErrorToast()
  } finally {
    dialogInstance?.close()
  }
}

function setDeliveryToClose(brandId: string, company: string) {
  selectedBrandAndCompanies.value[brandId] = [company]
  closingBrandsSelected.value = true
}

async function openDelivery(brandId: string, company: string) {
  const dialogInstance = dialog({
    icon: 'status',
    iconAnimation: 'animate-spin',
    title: t('close-delivery.opening'),
    noLabels: true,
    hiddenClose: true
  })
  try {
    const { failed } = await openDeliveries({
      [brandId]: [company]
    })
    showFailedNotifications(failed, 'open')

    if (failed.length === 0) {
      const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
      const brandName = brand?.brandName ?? ''

      toast({
        type: 'success',
        title: t('close-delivery.delivery-opened', {
          company: translateCompany(company),
          brandName
        })
      })
    }
  } catch {
    showConnectionErrorToast()
  } finally {
    dialogInstance?.close()
  }
}

async function startClosing(closingTime: ClosingTime) {
  isClosing.value = true
  try {
    const { closedUntil, failed } = await closeDeliveries(
      selectedBrandAndCompanies.value,
      closingTime
    )

    showFailedNotifications(failed, 'close')
    if (failed.length === 0) {
      showClosingNotifications(parseISO(closedUntil))
    }
  } catch {
    showConnectionErrorToast()
  } finally {
    selectedBrandAndCompanies.value = {}
    closingBrandsSelected.value = false
    isClosing.value = false
  }
}

function showConnectionErrorToast() {
  toast({
    type: 'error',
    title: t('close-delivery.connection-error'),
    duration: 10_000
  })
}

function showClosingNotifications(closedUntil: Date): void {
  const entries = Object.entries(selectedBrandAndCompanies.value)
  if (entries.length > 1) {
    toast({
      type: 'success',
      title: t('close-delivery.all-deliveries-closed'),
      message: timeFrom(closedUntil)
    })
  } else if (entries.length === 1) {
    const [brandId, companies] = entries[0]
    const brand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
    const brandName = brand?.brandName ?? ''

    if (companies.length > 1) {
      toast({
        type: 'success',
        title: t('close-delivery.brand-closed', { brandName }),
        message: timeFrom(closedUntil)
      })
    } else {
      toast({
        type: 'success',
        title: t('close-delivery.company-closed', {
          company: translateCompany(companies[0]),
          brandName
        }),
        message: timeFrom(closedUntil)
      })
    }
  }
}

function showFailedNotifications(
  failed: { brandId: string; company: string }[],
  action: 'close' | 'open'
) {
  for (const { company } of failed) {
    const key = action === 'close' ? 'close-delivery.failed-close' : 'close-delivery.failed-open'
    toast({
      type: 'error',
      title: t(key, { company: translateCompany(company) }),
      duration: 10_000
    })
  }
}

function timeFrom(date: Date) {
  if (differenceInDays(date, currentTime.value) > 365) {
    return t('close-delivery.indefinitely')
  } else {
    return formatDistance(date, currentTime.value)
  }
}
</script>
